import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  ArrowLeftOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  SendOutlined,
  SolutionOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { Link, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES } from "../../types/auth";
import type { TicketDetail, TicketMessage } from "../../types/ticket";
import { ticketService } from "../../services/api/ticketService";
import { notifyTicketsUnreadChanged, usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";
import { RichTextEditor } from "../../components/common/RichTextEditor";

const { Text } = Typography;

const STATUS_COLOR: Record<number, string> = {
  1: "blue",
  2: "green",
  3: "default",
};

const priorityColor = (p: string | undefined): string => {
  switch (p) {
    case "critical": return "red";
    case "high":     return "volcano";
    case "medium":   return "blue";
    case "low":      return "default";
    default:         return "default";
  }
};

const formatBytes = (n: number, t: (k: string, v?: any) => string) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return t("tickets.size_kb", { kb: (n / 1024).toFixed(1) });
  return t("tickets.size_mb", { mb: (n / 1024 / 1024).toFixed(1) });
};

export const TicketShow = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { message: notify } = AntdApp.useApp();
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  const listPath = isAdmin ? "/admin/tickets" : "/auditor/incidences";
  const myAppUserId = identity?.app_user?.id;

  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyHtml, setReplyHtml] = useState("");
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // The resolution modal collects optional notes (and, for audit
  // incidences, the regularized surface) the first time the ticket
  // transitions out of OPEN.
  const [resolutionOpen, setResolutionOpen] = useState<null | 2 | 3>(null);
  const [resolutionForm] = Form.useForm<{
    resolution_notes?: string;
    regularized_surface?: number | null;
  }>();

  // Whoever opened the ticket marks it resolved; admins close it after that.
  const isCreator = !!myAppUserId && ticket?.created_by === myAppUserId;
  // The "regularized surface" closure field is meaningful only for
  // surface-irregularity audit incidences. Match on the category name
  // so it covers categories admins seed themselves (e.g.
  // "Irregularidad de superficie", "Superficie irregular"…).
  const isSurfaceIrregularity = useMemo(() => {
    if (ticket?.category_kind !== "audit") return false;
    const name = (ticket?.category_name ?? "").toLowerCase();
    return name.includes("superficie");
  }, [ticket?.category_kind, ticket?.category_name]);
  const isOpen = ticket?.status === 1;
  const isResolved = ticket?.status === 2;
  const isClosed = ticket?.status === 3;
  // Once a ticket leaves OPEN, the conversation is frozen — admins
  // reopen explicitly if they need to follow up. This also matches
  // the user-facing wording: "cerrar la incidencia" = no more
  // messages.
  const isLocked = !!ticket && !isOpen;
  const hasResolution = useMemo(
    () =>
      !!ticket &&
      !!(
        ticket.resolved_date ||
        ticket.resolution_notes ||
        ticket.resolved_by_name ||
        ticket.regularized_surface != null
      ),
    [ticket],
  );

  usePageTitle(ticket ? `${ticket.code} — ${ticket.subject}` : t("tickets.title"));

  const reload = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await ticketService.getTicket(Number(id));
      setTicket(data);
      // Retrieving the detail marks unread messages as read on the
      // backend; nudge the sidebar so the badge updates without
      // waiting for the next 60s poll.
      notifyTicketsUnreadChanged();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSendReply = async () => {
    if (!ticket) return;
    if (!replyHtml || replyHtml === "<p><br></p>") return;
    setSending(true);
    try {
      await ticketService.addMessage(ticket.id, {
        body_html: replyHtml,
        attachments: files
          .map((f) => f.originFileObj as File | undefined)
          .filter((f): f is File => !!f),
      });
      notify.success(t("tickets.reply_success"));
      setReplyHtml("");
      setFiles([]);
      await reload();
    } catch {
      notify.error(t("tickets.reply_error"));
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (
    status: 1 | 2 | 3,
    resolution?: {
      resolution_notes?: string;
      regularized_surface?: number | null;
    },
  ) => {
    if (!ticket) return;
    setUpdatingStatus(true);
    try {
      await ticketService.updateStatus(ticket.id, status, resolution);
      notify.success(t("tickets.status_changed"));
      await reload();
    } catch {
      notify.error(t("tickets.reply_error"));
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Mark Resolved (creator) and Close (admin) both go through this
  // modal so a closure note + regularized surface (when applicable)
  // are captured the first time the ticket leaves OPEN.
  const openResolutionModal = (target: 2 | 3) => {
    resolutionForm.setFieldsValue({
      resolution_notes: ticket?.resolution_notes ?? "",
      regularized_surface: ticket?.regularized_surface ?? null,
    });
    setResolutionOpen(target);
  };

  const submitResolution = async () => {
    const target = resolutionOpen;
    if (!target) return;
    const values = await resolutionForm.validateFields();
    setResolutionOpen(null);
    await updateStatus(target, {
      resolution_notes: values.resolution_notes ?? "",
      regularized_surface:
        values.regularized_surface == null ? null : Number(values.regularized_surface),
    });
  };

  if (loading && !ticket) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }
  if (!ticket) {
    return (
      <Card>
        <Text type="danger">404</Text>
      </Card>
    );
  }

  return (
    <Card>
      <PageHeader
        icon={<WarningOutlined />}
        accent="#FA8C16"
        title={
          <span>
            <Tag color="#FF8C00" style={{ marginRight: 8 }}>{ticket.code}</Tag>
            {ticket.subject}
          </span>
        }
        subtitle={
          <Space size={8} wrap>
            <Tag>{ticket.category_name}</Tag>
            <Tag color={STATUS_COLOR[ticket.status]}>
              {t(`tickets.status_${ticket.status}`)}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("tickets.created_by")}: {ticket.created_by_name ?? "—"}
            </Text>
          </Space>
        }
        extra={
          <Space wrap>
            {/* Contact-proposal deep link: admins land directly on the
                validation page with the apply modal pre-opened on this
                ticket. Only shown for open Contactos-category tickets
                — once resolved/closed the action no longer applies. */}
            {isAdmin &&
              ticket.status === 1 &&
              ticket.category_name === "Contactos" && (
                <Link
                  to={`/admin/contact-proposals?ticket=${ticket.id}`}
                >
                  <Button type="primary" icon={<SolutionOutlined />}>
                    {t(
                      "tickets.validate_contact_proposal",
                      "Validar propuesta de contacto",
                    )}
                  </Button>
                </Link>
              )}
            {/* Reopen: anyone with edit power on the ticket, when not Open */}
            {(isAdmin || isCreator) && ticket.status !== 1 && ticket.status !== 3 && (
              <Button
                icon={<ReloadOutlined />}
                onClick={() => updateStatus(1)}
                loading={updatingStatus}
              >
                {t("tickets.reopen")}
              </Button>
            )}
            {/* Closed → only admin can reopen */}
            {isAdmin && ticket.status === 3 && (
              <Button
                icon={<ReloadOutlined />}
                onClick={() => updateStatus(1)}
                loading={updatingStatus}
              >
                {t("tickets.reopen")}
              </Button>
            )}
            {/* Mark resolved: creator (or admin) only, only while Open */}
            {(isCreator || isAdmin) && isOpen && (
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => openResolutionModal(2)}
                loading={updatingStatus}
              >
                {t("tickets.mark_resolved")}
              </Button>
            )}
            {/* Close: admin only, only after Resolved */}
            {isAdmin && isResolved && (
              <Button
                icon={<CloseOutlined />}
                onClick={() => openResolutionModal(3)}
                loading={updatingStatus}
                danger
              >
                {t("tickets.close")}
              </Button>
            )}
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(listPath)}
              style={{ borderRadius: 999, paddingInline: 16 }}
            >
              {t("tickets.back_to_list")}
            </Button>
          </Space>
        }
      />

      {/* Información clave: panel siempre visible con los campos
          más útiles para revisar la incidencia sin tener que
          inferir nada del hilo de mensajes. */}
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={isLocked ? 16 : 24}>
          <Card
            type="inner"
            title={t("tickets.details", "Información")}
            styles={{ body: { padding: 20 } }}
          >
            <Descriptions
              column={{ xs: 1, sm: 2, md: 2, lg: 3 }}
              colon={false}
              layout="vertical"
              labelStyle={{
                color: "#8c8c8c",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                paddingBottom: 4,
              }}
              contentStyle={{ paddingBottom: 14 }}
            >
              <Descriptions.Item label={t("tickets.kind", "Origen")}>
                {ticket.category_kind === "audit" ? (
                  <Tag color="orange">{t("tickets.kind_audit", "Auditoría")}</Tag>
                ) : ticket.category_kind === "support" ? (
                  <Tag color="blue">{t("tickets.kind_support", "Soporte")}</Tag>
                ) : "—"}
              </Descriptions.Item>
              <Descriptions.Item label={t("tickets.category", "Categoría")}>
                <Tag>{ticket.category_name}</Tag>
              </Descriptions.Item>
              {ticket.priority && (
                <Descriptions.Item label={t("tickets.priority", "Prioridad")}>
                  <Tag color={priorityColor(ticket.priority)}>
                    {ticket.priority_display ?? ticket.priority}
                  </Tag>
                </Descriptions.Item>
              )}
              {ticket.requires_immediate_action && (
                <Descriptions.Item label={t("tickets.urgent", "Acción inmediata")}>
                  <Tag color="red" icon={<ExclamationCircleOutlined />}>
                    {t("common.yes", "Sí")}
                  </Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t("tickets.created_by", "Creado por")}>
                {ticket.created_by_name ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label={t("tickets.created_at", "Fecha")}>
                {dayjs(ticket.created).format("DD/MM/YYYY HH:mm")}
              </Descriptions.Item>
              {ticket.assigned_to_name && (
                <Descriptions.Item label={t("tickets.assigned_to", "Asignado a")}>
                  {ticket.assigned_to_name}
                </Descriptions.Item>
              )}
              {ticket.deadline && (
                <Descriptions.Item label={t("tickets.deadline", "Fecha límite")}>
                  {dayjs(ticket.deadline).format("DD/MM/YYYY")}
                </Descriptions.Item>
              )}
              {/* Audit context — only when this ticket is an audit
                  incidence. The reference links to the audit detail
                  (replaces the row click in the list). */}
              {ticket.audit && (
                <Descriptions.Item
                  label={t("tickets.audit", "Auditoría")}
                  span={3}
                >
                  <Space size={8} wrap>
                    <Button
                      type="link"
                      icon={<AuditOutlined />}
                      style={{ padding: 0 }}
                      onClick={() => navigate(`/audits/${ticket.audit}`)}
                    >
                      {ticket.audit_reference ?? `#${ticket.audit}`}
                    </Button>
                    {ticket.producer_name && (
                      <Text type="secondary">· {ticket.producer_name}</Text>
                    )}
                    {ticket.farm_name && (
                      <Text type="secondary">· {ticket.farm_name}</Text>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              {/* Producer-only context — solicitudes de auditoría de
                  ESA cuando aún no hay audit creada. Link directo a
                  la ficha del productor para agilizar el trabajo del
                  admin que responde. */}
              {ticket.producer_id && !ticket.audit && (
                <Descriptions.Item
                  label={t(
                    "tickets.producer_to_audit",
                    "Productor a auditar",
                  )}
                  span={3}
                >
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() =>
                      navigate(`/producers/show/${ticket.producer_id}`)
                    }
                  >
                    {ticket.producer_name ?? `#${ticket.producer_id}`}
                  </Button>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
        {/* Resolution panel — visible from the moment the ticket
            leaves OPEN, even if the closing user didn't capture
            notes. Hidden entirely while OPEN so the layout doesn't
            waste a column on an empty placeholder. */}
        {isLocked && (
          <Col xs={24} lg={8}>
            <Card
              type="inner"
              title={
                <Space size={8}>
                  <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  {t("tickets.resolution", "Resolución")}
                </Space>
              }
              styles={{
                header: { background: "#f6ffed" },
                body: { padding: 20 },
              }}
            >
              {ticket.resolved_date && (
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("tickets.resolved_at", "Cerrada el")}:
                  </Text>{" "}
                  <strong>{dayjs(ticket.resolved_date).format("DD/MM/YYYY HH:mm")}</strong>
                </div>
              )}
              {ticket.resolved_by_name && (
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("tickets.resolved_by", "Por")}:
                  </Text>{" "}
                  {ticket.resolved_by_name}
                </div>
              )}
              {ticket.regularized_surface != null && (
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("tickets.regularized_surface", "Superficie regularizada")}:
                  </Text>{" "}
                  <strong>{ticket.regularized_surface} ha</strong>
                </div>
              )}
              {ticket.resolution_notes ? (
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px dashed #d9d9d9",
                    whiteSpace: "pre-wrap",
                    // Romper palabras larguísimas (URLs, hashes,
                    // identificadores) para que no rompan el ancho
                    // de la card.
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    fontSize: 13,
                    lineHeight: 1.55,
                  }}
                >
                  {ticket.resolution_notes}
                </div>
              ) : !hasResolution ? (
                <Text type="secondary" italic style={{ fontSize: 12 }}>
                  {t("tickets.no_resolution_notes", "Sin notas de resolución.")}
                </Text>
              ) : null}
            </Card>
          </Col>
        )}
      </Row>

      {/* Cuerpo inicial — para incidencias creadas desde el modal de
          auditoría, el cuerpo va aquí inline en lugar de en un
          mensaje. Mostramos también el adjunto principal si existe. */}
      {(ticket.description || ticket.attachment_url) && (
        <Card
          type="inner"
          size="small"
          title={t("tickets.message_body", "Descripción")}
          style={{ marginBottom: 16 }}
        >
          {ticket.description && (
            <div
              className="ticket-message-body"
              dangerouslySetInnerHTML={{ __html: ticket.description }}
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                marginBottom: 12,
                // HTML del usuario: URLs / hashes largos sin espacios
                // tienen que romper en lugar de salirse de la card.
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            />
          )}
          {ticket.attachment_url && (
            <a
              href={ticket.attachment_url}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Tag icon={<PaperClipOutlined />} style={{ cursor: "pointer" }}>
                {t("tickets.download_attachment", "Descargar archivo adjunto")}
              </Tag>
            </a>
          )}
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {ticket.messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            isMine={m.sender === identity?.app_user?.id}
          />
        ))}
      </div>

      {isOpen ? (
        <Card type="inner" title={t("tickets.reply")} style={{ marginTop: 24 }}>
          <RichTextEditor
            value={replyHtml}
            onChange={setReplyHtml}
            placeholder={t("tickets.reply_placeholder")}
            minHeight={140}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Upload
              beforeUpload={() => false}
              multiple
              fileList={files}
              onChange={({ fileList }) => setFiles(fileList)}
            >
              <Button icon={<PaperClipOutlined />} size="small">
                {t("tickets.add_attachment")}
              </Button>
            </Upload>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={onSendReply}
              loading={sending}
              disabled={!replyHtml || replyHtml === "<p><br></p>"}
            >
              {t("tickets.send_reply")}
            </Button>
          </div>
        </Card>
      ) : (
        // Conversation is frozen — the only thing the user can do is
        // reopen (handled by the header buttons). Keep this notice
        // visible so it's obvious *why* the reply box is gone.
        <Alert
          type={isClosed ? "success" : "info"}
          showIcon
          icon={<CheckCircleOutlined />}
          style={{ marginTop: 24 }}
          message={
            isClosed
              ? t("tickets.locked_closed", "Incidencia cerrada — no se admiten nuevos mensajes.")
              : t("tickets.locked_resolved", "Incidencia resuelta — no se admiten nuevos mensajes. Un administrador podrá cerrarla o reabrirla.")
          }
        />
      )}

      <Modal
        title={
          resolutionOpen === 3
            ? t("tickets.close_modal_title", "Cerrar incidencia")
            : t("tickets.resolve_modal_title", "Marcar como resuelta")
        }
        open={resolutionOpen !== null}
        onCancel={() => setResolutionOpen(null)}
        onOk={submitResolution}
        confirmLoading={updatingStatus}
        okText={
          resolutionOpen === 3
            ? t("tickets.close", "Cerrar")
            : t("tickets.mark_resolved", "Marcar como resuelta")
        }
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnClose
      >
        <Form
          form={resolutionForm}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="resolution_notes"
            label={t("tickets.resolution_notes", "Notas de resolución")}
            tooltip={t(
              "tickets.resolution_notes_tooltip",
              "Resumen visible en la ficha; ayuda a saber por qué se cerró sin tener que leer el hilo.",
            )}
          >
            <Input.TextArea
              rows={4}
              placeholder={t(
                "tickets.resolution_notes_placeholder",
                "Qué se ha hecho para resolver la incidencia",
              )}
            />
          </Form.Item>
          {isSurfaceIrregularity && (
            <Form.Item
              name="regularized_surface"
              label={t("tickets.regularized_surface", "Superficie regularizada")}
              extra="ha"
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: "100%" }}
                placeholder="0.00"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

const MessageBubble = ({
  message,
  isMine,
}: {
  message: TicketMessage;
  isMine: boolean;
}) => {
  const { t } = useTranslation();
  const senderRoleColor = message.sender_role === 2 ? "geekblue" : "blue";
  const bg = isMine ? "#fff7e6" : "#fafafa";
  const borderColor = isMine ? "#ffd591" : "#f0f0f0";

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexDirection: isMine ? "row-reverse" : "row",
      }}
    >
      <Avatar
        size={40}
        src={message.sender_avatar_url ?? undefined}
        icon={!message.sender_avatar_url && <UserOutlined />}
      />
      <div
        style={{
          flex: 1,
          maxWidth: "75%",
          backgroundColor: bg,
          border: `1px solid ${borderColor}`,
          borderRadius: 10,
          padding: 14,
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Space size={6}>
            <strong>{isMine ? t("tickets.you") : message.sender_name}</strong>
            <Tag color={senderRoleColor} style={{ marginInlineEnd: 0 }}>
              {message.sender_role_display}
            </Tag>
            {!message.is_read && (
              <Tooltip title={t("tickets.unread")}>
                <Tag color="orange">●</Tag>
              </Tooltip>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(message.created).format("DD/MM/YYYY HH:mm")}
          </Text>
        </div>
        <div
          className="ticket-message-body"
          dangerouslySetInnerHTML={{ __html: message.body_html }}
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            // Mismo motivo que en description y resolution_notes:
            // URLs / hashes largos sin espacios desbordan la
            // burbuja del mensaje sin esto.
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        />
        {message.attachments.length > 0 && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${borderColor}`, paddingTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
              {t("tickets.attachments")}
            </Text>
            <Space size={[6, 6]} wrap>
              {message.attachments.map((a) => (
                <a
                  key={a.id}
                  href={a.url ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Tag icon={<PaperClipOutlined />} style={{ cursor: "pointer" }}>
                    {a.filename}{" "}
                    <span style={{ color: "#8c8c8c", fontSize: 11 }}>
                      ({formatSize(a.size, t)})
                    </span>
                  </Tag>
                </a>
              ))}
            </Space>
          </div>
        )}
      </div>
    </div>
  );
};

const formatSize = (n: number, t: (k: string, v?: any) => string) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return t("tickets.size_kb", { kb: (n / 1024).toFixed(1) });
  return t("tickets.size_mb", { mb: (n / 1024 / 1024).toFixed(1) });
};

export default TicketShow;
